/* Angular Component */
import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
} from "@angular/core";
import { UI } from "../../../services/ui.service";
import { ActivatedRoute, Router } from "@angular/router";
import libraryStore from "../../../services/stores/library.store";
import { GraphicsRepository } from "../../../domain/graphics-repository";
import { MediaRepository } from "../../../domain/media-repository";
import { SearchService } from "../../../services/search.service";
import { from, Observable } from "rxjs";
import { HttpService } from "../../../infrastructure/http/http";
import { Logger } from "../../../infrastructure/logger";
import { MediaItem } from "../../../domain/media";
import { LiveFeedRepository } from "../../../domain/live-feed-repository";
import { DynamicPromoRepository } from "../../../domain/dynamic-promo-repository";
import { SchedulerAsideService } from "./scheduler-aside-service";
import { TenantRepository } from "../../../domain/tenant-repository";
import { ILayoutsRepository } from "../../../domain/layouts/layouts.repository";
import { IBlockRepository } from "../../../domain/blocks/i-block.repository";
import { LibraryTypes } from "../../../domain/enums";
import { IBroadcastsRepository } from "../../../domain/broadcasts/i-broadcasts-repository";

const LIBRARIES = {
  video: "loadVideoLibrary",
  graphics: "loadGraphicsLibrary",
  ads: "loadAdsLibrary",
  feeds: "loadFeedsLibrary",
  dynamicPromo: "loadDynamicPromosLibrary",
};

const TAG = "SchedulerAside:";

@Component({
  selector: "scheduler-aside",
  templateUrl: "./schedulerAside.component.html",
  styleUrls: ["./schedulerAside.component.scss"],
  providers: [UI],
  host: {
    "[class.collapsed]": "isCollapsed()",
  },
})
export class SchedulerAsideComponent {
  selectedTab;
  offset = 0;
  libraryStore = libraryStore;
  schedulerLibrarySubject;

  @Input()
  showLibraryItemMenuButtons = true;

  onScrollChanged;
  Ob: Observable<any>;
  private searchSubscription: any;
  private liveStatusSubscription: any;

  //@HostBinding("class.collapsed") isCollapsed: Boolean = false;

  constructor(
    private dynamicPromoRepository: DynamicPromoRepository,
    private liveFeedRepository: LiveFeedRepository,
    private graphicsRepository: GraphicsRepository,
    private changeDetectorRef: ChangeDetectorRef,
    private ui: UI,
    private mediaRepository: MediaRepository,
    private tenantRepository: TenantRepository,
    private searchService: SearchService,
    private logger: Logger,
    private schedulerAsideService: SchedulerAsideService,
    private _layoutsRepository: ILayoutsRepository,
    private _blocksRepository: IBlockRepository,
    private _broadcastsRepository: IBroadcastsRepository,
  ) {}

  ngOnDestroy() {
    if (this.schedulerLibrarySubject) {
      this.schedulerLibrarySubject.unsubscribe();
    }
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.liveStatusSubscription) {
      this.liveStatusSubscription.unsubscribe();
    }
    this.Ob = null;
  }

  isCollapsed() {
    return this.schedulerAsideService.isCollapsed();
  }

  /**
   * Initializing component
   * @return void
   */
  ngOnInit() {
    let currentLibraryType = this.libraryStore.getLibraryType();

    this.schedulerLibrarySubject = this.libraryStore.libraryChanged.subscribe(
      (type) => {
        if (currentLibraryType !== type) {
          this.searchService.searchQuery = "";
          this.searchService.searchServiceSubject.next(true);
          currentLibraryType = type;
        }
        this.loadRelevantLibrary(type);
      },
    );

    /* Assign Library Type */
    if (
      currentLibraryType === "" ||
      currentLibraryType === LibraryTypes.AutopilotRules
    ) {
      this.libraryStore.setLibraryType(LibraryTypes.Video);
    }
    this.Ob = from(this.searchService.searchServiceSubject);
    this.searchSubscription = this.Ob.subscribe(() => {
      this.offset = 0;
      this.searchService
        .search(this.offset)
        .then((response: any) => {
          this.libraryStore.setLibraryItems(response);
          this.changeDetectorRef.markForCheck();
        })
        .catch((err) => {
          this.logger.error("Could not get media. Error: " + err);
        });
    });

    /* Minimzing Mam when channel go live */
    this.liveStatusSubscription = this.ui.EVENTS.LiveStatusDidChange.subscribe(
      (newStatus) => {
        if (newStatus) {
          this.collapse();
          this.minimizeToggle();
        }
      },
    );
  }

  /**
   * Loading the relevant library
   * @return void
   */
  loadRelevantLibrary(libraryType) {
    if (typeof libraryType != "string") return;

    /* Prepare library */
    const method = libraryType.toLowerCase() + "Library";

    /* Validate method and dispatch */
    if (typeof this[method] == "function") this[method].call(this);

    this.selectedTab = libraryType;
  }

  liveLibrary() {
    this.liveFeedRepository
      .getLiveFeeds()
      .then((response) => {
        this.libraryStore.setLibraryItems(response);
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        this.logger.error("Could not get live feeds. Error: " + err);
      });
  }

  dynamicpromoLibrary() {
    this.dynamicPromoRepository
      .getDynamicPromos()
      .then((response) => {
        this.libraryStore.setLibraryItems(response);
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        this.logger.error("Could not get dynamic promos. Error: " + err);
      });
  }

  /**
   * Loading View Library from remote server
   * @return void
   */
  videoLibrary() {
    const $this = this;

    this.mediaRepository
      .getMedia(
        this.searchService.searchQuery,
        0,
        0,
        "Name",
        null,
        null,
        null,
        null,
        "All",
        [],
        [],
        [],
        [],
      )
      .then((response) => {
        this.libraryStore.setLibraryItems(response);
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        this.logger.error("Could not get media. Error: " + err);
      });
  }

  /**
   * Load Graphics Library from remote server
   * @return void
   */
  graphicsLibrary() {
    this.graphicsRepository
      .getAll()
      .then(
        ((items) => {
          this.libraryStore.setLibraryItems(items);
          this.changeDetectorRef.markForCheck();
        }).bind(this),
        function (error) {
          console.error(TAG, "failed to load graphics from server: ", error);
        },
      )
      .catch(function (error) {
        console.error(TAG, "catching an error while loading graphics: ", error);
      });
  }

  layoutsLibrary() {
    const $this = this;

    this._layoutsRepository
      .getLayouts(this.searchService.searchQuery)
      .then((response) => {
        this.libraryStore.setLibraryItems(response);
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        this.logger.error("Could not get layouts. Error: " + err);
      });
  }
  blocksLibrary() {
    const $this = this;

    this._blocksRepository
      .get(this.searchService.searchQuery)
      .then((response) => {
        this.libraryStore.setLibraryItems(response);
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        this.logger.error("Could not get blocks. Error: " + err);
      });
  }
  broadcastsLibrary() {
    this._broadcastsRepository
      .get(this.searchService.searchQuery)
      .then((response) => {
        this.libraryStore.setLibraryItems(response);
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        this.logger.error("Could not get broadcasts. Error: " + err);
      });
  }

  loadGraphicsLibraryWithId(id) {
    // this.router.navigate(["scheduler/graphics", id]);
  }

  /**
   * Loading Ads Library From Remote Server
   * @return void
   */
  adsLibrary() {
    console.log("loadAdsLibrary)");
  }

  /**
   * Loading Feeds Library From Remote Server
   * @return void
   */
  feedsLibrary() {
    console.log("loadFeedsLibrary");
  }

  /**
   * Dispach internal message about error
   * @return void
   */
  onConnectionError() {
    /* Fire Notification about error */
    this.ui.notify(this.ui.EVENTS.ShowAlert, {
      title: "Server Connectivity!",
      message: "It's a shame but something had happen. Please reload the App!",
    });
  }

  /**
   * onSelectTab provides a listener to click event (tab selection)
   * @return void
   */
  onSelectTab(tabName) {
    /* Bullet proof.. */
    if (this.selectedTab == tabName) return;

    this.selectedTab = tabName;
    this.loadRelevantLibrary(tabName);
  }

  /**
   * Minimize / Maximize the sidebar
   * @param event
   * @return void
   */
  minimizeToggle() {
    let collapsed = !this.schedulerAsideService.isCollapsed();
    this.schedulerAsideService.setCollapse(collapsed);

    /* Notify UI Subscribers */
    this.ui.EVENTS.MamCollapsed.next(collapsed);
  }

  collapse() {
    this.schedulerAsideService.setCollapse(true);

    /* Notify UI Subscribers */
    this.ui.EVENTS.MamCollapsed.next(this);
  }

  onScroll() {
    if (this.searchService.searchMode === "video") {
      this.offset += 20;
      this.searchService
        .search(this.offset)
        .then((response: MediaItem[]) => {
          for (let i = 0; i < response.length; i++) {
            for (let j = 0; j < this.libraryStore.libraryItems.length; j++) {
              if (response[i].id === this.libraryStore.libraryItems[j].id) {
                response.splice(i, 1);
                i--;
                break;
              }
            }
          }
          this.libraryStore.setLibraryItems(
            this.libraryStore.libraryItems.concat(response),
          );
          this.changeDetectorRef.markForCheck();
        })
        .catch((err) => {
          this.logger.error("Could not get media. Error: " + err);
        });
    }
  }
}
